export const LIST_TYPE_LABELS = {
  tracking_report: 'Tracking Report',
  reallocation_list: 'Reallocation List',
  potential_qualifiers: 'Potential Qualifiers',
  long_list: 'Official Long List',
  final_rankings: 'Final Rankings',
  final_field: 'Final Field',
};

export const GENDER_LABELS = {
  men: 'Men',
  women: 'Women',
};

export const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Uppercase date/time for hero strip (UTC). */
export const formatDateTimeUtc = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  const formatted = d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
  return formatted.toUpperCase();
};

/**
 * Formats a stored UTC date/ISO string as a human-readable Swiss (Europe/Zurich)
 * local date+time, e.g. "Mon, 29 Jun 2026, 14:00 (Swiss time)".
 */
export const formatSwissDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Zurich',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return `${formatted} (Swiss time)`;
};

/**
 * Converts a stored UTC date/ISO string into the value expected by an
 * `<input type="datetime-local">`, expressed in Europe/Zurich wall-clock time.
 */
export const utcToZurichInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Zurich',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const map = {};
  parts.forEach(({ type, value: v }) => {
    map[type] = v;
  });
  const hour = map.hour === '24' ? '00' : map.hour;
  return `${map.year}-${map.month}-${map.day}T${hour}:${map.minute}`;
};

/**
 * Converts a `<input type="datetime-local">` value (interpreted as Swiss
 * Europe/Zurich wall-clock time, honoring DST) into a UTC Date instance
 * suitable for sending to the server.
 */
export const zurichInputValueToUtc = (localValue) => {
  if (!localValue) return null;
  const naiveUtc = new Date(`${localValue}:00Z`);
  if (Number.isNaN(naiveUtc.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Zurich',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(naiveUtc);
  const map = {};
  parts.forEach(({ type, value: v }) => {
    map[type] = v;
  });
  const hour = map.hour === '24' ? '00' : map.hour;
  const zurichAsUtc = new Date(
    `${map.year}-${map.month}-${map.day}T${hour}:${map.minute}:${map.second}Z`
  );

  const offsetMs = naiveUtc.getTime() - zurichAsUtc.getTime();
  return new Date(naiveUtc.getTime() + offsetMs);
};

export const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Reads the current wall-clock date/time in Europe/Zurich. */
const getZurichNowParts = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Zurich',
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const map = {};
  parts.forEach(({ type, value }) => {
    map[type] = value;
  });
  return {
    dayOfWeek: WEEKDAY_NAMES.indexOf(map.weekday),
    hour: map.hour === '24' ? 0 : Number(map.hour),
    minute: Number(map.minute),
  };
};

/**
 * Computes the next UTC instant at which a weekly recurring schedule
 * (given as a Europe/Zurich weekday + hour + minute) will next fire,
 * for display purposes only (the server independently evaluates this).
 */
export const nextZurichWeeklyOccurrence = (dayOfWeek, hour, minute, from = new Date()) => {
  if (![dayOfWeek, hour, minute].every((n) => Number.isInteger(n))) return null;

  for (let dayOffset = 0; dayOffset <= 7; dayOffset += 1) {
    const candidate = new Date(from.getTime() + dayOffset * 24 * 60 * 60 * 1000);
    const parts = getZurichNowParts(candidate);
    if (parts.dayOfWeek !== dayOfWeek) continue;

    const candidateLocalStr = utcToZurichInputValue(candidate).slice(0, 10);
    const target = zurichInputValueToUtc(
      `${candidateLocalStr}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    );
    if (target && target.getTime() >= from.getTime() - 60000) {
      return target;
    }
  }
  return null;
};
